import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { api } from "@/lib/api";

const initial = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contacts", form);
      toast.success("Message sent. We'll be in touch shortly.");
      setForm(initial);
    } catch (err) {
      toast.error("Unable to send. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#050505]">
      {/* Hero */}
      <section className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="label-eyebrow mb-6 fade-up">Contact</div>
          <h1
            className="font-display text-white max-w-4xl leading-[1.05] fade-up fade-up-delay-1"
            style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)", fontWeight: 300 }}
          >
            Let's chat about your <span className="italic text-[#D4AF37]">next vehicle</span>.
          </h1>
          <p className="text-white/65 max-w-2xl mt-8 text-lg fade-up fade-up-delay-2">
            Send a message and our team will respond within 24 to 48 hours. Or reach us directly
            using the details below.
          </p>
        </div>
      </section>

      {/* Form + info */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Form */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-[#0a0a0a] border border-white/10 p-8 md:p-10 space-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full Name *">
                <input
                  data-testid="contact-name"
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  className={inputCls}
                  required
                />
              </Field>
              <Field label="Email *">
                <input
                  data-testid="contact-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  className={inputCls}
                  required
                />
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Phone">
                <input
                  data-testid="contact-phone"
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  className={inputCls}
                />
              </Field>
              <Field label="Subject *">
                <input
                  data-testid="contact-subject"
                  type="text"
                  value={form.subject}
                  onChange={update("subject")}
                  className={inputCls}
                  required
                />
              </Field>
            </div>
            <Field label="Message *">
              <textarea
                data-testid="contact-message"
                value={form.message}
                onChange={update("message")}
                rows="6"
                className={`${inputCls} resize-none`}
                required
              />
            </Field>

            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="w-full bg-[#D4AF37] text-black font-medium py-4 hover:bg-[#F6ECCC] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? "Sending..." : (<>Send Message <Send size={16} /></>)}
            </button>
          </form>

          {/* Info */}
          <aside className="lg:col-span-5 space-y-6">
            <InfoCard
              icon={<Mail size={18} />}
              label="Email"
              value="admin@keystoown.au"
              href="mailto:admin@keystoown.au"
              testId="info-email"
            />
            <InfoCard
              icon={<Phone size={18} />}
              label="Phone"
              value="0422 254 888"
              href="tel:0422254888"
              testId="info-phone"
            />
            <InfoCard
              icon={<MapPin size={18} />}
              label="Service Areas"
              value="Sydney · Melbourne · Brisbane · Perth · Adelaide · Canberra · Hobart · Darwin"
              testId="info-areas"
            />

            <div className="border border-[#D4AF37]/30 p-6 bg-gradient-to-br from-[#0a0a0a] to-[#141004]">
              <div className="label-eyebrow mb-3">Office Hours</div>
              <p className="text-white/80 text-sm leading-relaxed">
                Monday – Friday: 9:00 AM – 6:00 PM AEST <br />
                Saturday: 10:00 AM – 3:00 PM AEST <br />
                Sunday: Closed
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-[#050505] border border-white/10 text-white px-4 py-3 text-sm focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-white/50 mb-2 block">
        {label}
      </span>
      {children}
    </label>
  );
}

function InfoCard({ icon, label, value, href, testId }) {
  const inner = (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] flex-shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-1">{label}</div>
        <div className="text-white text-sm leading-relaxed">{value}</div>
      </div>
    </div>
  );
  if (href) {
    return (
      <a
        href={href}
        data-testid={testId}
        className="block border border-white/10 p-6 bg-[#0a0a0a] hover:border-[#D4AF37]/40 transition-colors"
      >
        {inner}
      </a>
    );
  }
  return (
    <div data-testid={testId} className="border border-white/10 p-6 bg-[#0a0a0a]">
      {inner}
    </div>
  );
}
